import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import { TextField } from "../../../../ui/components/TextField";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { CustomerGroup } from "../../../../shared/data/api/currenciesApi";
import { CustomerEntity } from "../../domain/entities/CustomerEntity";
import CreateCustomerGroupModal from "../../../../components/CreateCustomerGroupModal";
import { Loader } from "../../../../ui/components/Loader";
import {
  useCustomerGroups,
  useCustomerDetails,
  useCustomerOperations,
} from "../hooks";
import {
  fetchCurrencies,
  Currency,
} from "../../../../shared/data/api/currenciesApi";
import {
  SearchableDropdown,
  ErrorBanner,
  useToast,
  ReviewRequestModal,
} from "../../../../shared/presentation/components";
import {
  useErrorHandling,
  usePermissions,
} from "../../../../shared/presentation/hooks";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { FeatherChevronDown } from "@subframe/core";
import CustomerContextSection from "../../../../shared/presentation/components/CustomerContextSection";
import {
  CustomerContextData,
  getCustomerContext,
  saveCustomerContext,
} from "../../../../services/contextApi";
import GstinVerificationField from "../../../../shared/presentation/components/GstinVerificationField";
import GSTINPrefillDialog from "../../../../components/GSTINPrefillDialog";
import { useGstinVerification } from "../../../../shared/presentation/hooks/useGstinVerification";
import { getCompany } from "../../../../shared/data/storage/authStorage";
import { getStateAbbreviation } from "../../../../utils";
import {
  buildManualInvoiceCustomerPatch,
  isCreateManualInvoiceReturnState,
} from "../utils/manualInvoiceCustomerPatch";

import {
  COUNTRIES,
  findCountryByCode,
} from "../../../../shared/constants/countries";
import {
  INDIAN_STATES,
  findIndianStateByCode,
} from "../../../../shared/constants/indianStates";
import { getCurrencyIdForCountry } from "../../../../shared/utils/countryCurrency";
import {
  buildCustomerGstinPrefillFields,
  buildCustomerGstinPrefillUpdates,
} from "../../../../shared/utils/customerGstinPrefill";

const EditCustomerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get customer information from navigation state
  const customerId = location.state?.customerId;
  const customerIdInt = location.state?.customerIdInt;
  const manualInvoiceReturn = isCreateManualInvoiceReturnState(location.state)
    ? location.state
    : null;

  // Form state
  const [formData, setFormData] = useState<Partial<CustomerEntity>>({});
  const [originalCustomer, setOriginalCustomer] =
    useState<Partial<CustomerEntity> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);

  // Review workflow state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<Partial<CustomerEntity> | null>(null);

  // Customer context state
  const [customerContextData, setCustomerContextData] =
    useState<CustomerContextData>({});
  const [existingCustomerContext, setExistingCustomerContext] = useState<
    CustomerContextData | undefined
  >(undefined);

  // Error handling with new hook
  const { errors, setFieldErrors, setApiErrors, clearErrors, clearFieldError } =
    useErrorHandling();

  // Toast notifications
  const { showSuccess } = useToast();

  // Permission hooks
  const { hasPermission, requiresReview } = usePermissions();
  const canUpdate = hasPermission(MODULE_CODES.CUSTOMER, ACTION_CODES.UPDATE);
  const needsReview = requiresReview(
    MODULE_CODES.CUSTOMER,
    ACTION_CODES.UPDATE,
  );

  const {
    customer,
    loading: isFetchingCustomer,
    error: fetchError,
    fetchCustomer,
  } = useCustomerDetails();
  const { updateCustomer } = useCustomerOperations();
  const {
    customerGroups,
    addCustomerGroup,
    loading: customerGroupsLoading,
    error: customerGroupsError,
  } = useCustomerGroups();

  // GSTIN verification
  const gstinVerification = useGstinVerification({
    entityName: (formData.customer_name as string) || "",
    entityType: "customer",
  });
  const { checkExisting } = gstinVerification;

  // Prefill dialog state
  const [showPrefillDialog, setShowPrefillDialog] = useState(false);
  const [userTriggeredVerify, setUserTriggeredVerify] = useState(false);

  // Show prefill dialog when user-triggered GSTIN verification succeeds
  useEffect(() => {
    if (
      userTriggeredVerify &&
      gstinVerification.status === "verified" &&
      gstinVerification.verificationData?.status === "Active"
    ) {
      setShowPrefillDialog(true);
      setUserTriggeredVerify(false);
    }
  }, [
    userTriggeredVerify,
    gstinVerification.status,
    gstinVerification.verificationData,
  ]);

  // Fetch customer data on component mount
  useEffect(() => {
    if (customerId) {
      fetchCustomer(customerId);
    }
  }, [customerId, fetchCustomer]);

  // Fetch existing customer context
  useEffect(() => {
    if (customerIdInt) {
      getCustomerContext(customerIdInt)
        .then((response) => {
          if (response.success && response.data) {
            setExistingCustomerContext(response.data);
            setCustomerContextData(response.data);
          }
        })
        .catch((error) => {
          console.warn("Failed to load customer context:", error);
        });
    }
  }, [customerIdInt]);

  // Update form data when customer is loaded
  useEffect(() => {
    if (customer) {
      const customerData = { ...customer };
      // Clear GSTIN if GST treatment is not business_gst
      if (
        customerData.gst_treatment &&
        customerData.gst_treatment !== "business_gst"
      ) {
        customerData.gstin_vat_tax_id = "";
      }
      setOriginalCustomer({ ...customerData }); // Store original for field changes
      setFormData(customerData);
      // Check if customer's GSTIN is already verified
      if (
        customerData.gstin_vat_tax_id &&
        customerData.gst_treatment === "business_gst"
      ) {
        checkExisting(customerData.gstin_vat_tax_id);
      }
    }
  }, [customer, checkExisting]);

  // Fetch currencies on component mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        const response = await fetchCurrencies();
        setCurrencies(response.data);
      } catch (error) {
        console.error("Error fetching currencies:", error);
        setCurrenciesError("Failed to load currencies");
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [selectKey, setSelectKey] = useState(0);

  const handleApplyGstinPrefill = () => {
    const updates = buildCustomerGstinPrefillUpdates(
      gstinVerification.verificationData,
      formData.gstin_vat_tax_id || "",
      currencies,
    );
    setFormData((prev) => ({ ...prev, ...updates }));
    (
      [
        "customer_name",
        "billing_address",
        "state",
        "city",
        "postal_code",
        "country",
        "pan_tax_registration_no",
        "currency_id",
      ] as const
    ).forEach(clearFieldError);
    setShowPrefillDialog(false);
  };

  const handleInputChange = (
    field: keyof CustomerEntity,
    value: string | number,
  ) => {
    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Clear GSTIN when GST treatment is not Business GST
      if (field === "gst_treatment" && value !== "business_gst") {
        newData.gstin_vat_tax_id = "";
        gstinVerification.reset();
      }

      // Reset GSTIN verification when GSTIN changes
      if (field === "gstin_vat_tax_id") {
        gstinVerification.reset();
      }

      if (field === "country") {
        if (value !== "IN") {
          newData.state = "";
        }
        const currencyId = getCurrencyIdForCountry(value as string, currencies);
        if (currencyId) {
          newData.currency_id = currencyId;
        }
      }

      return newData;
    });

    // Clear field error when user starts typing
    clearFieldError(field);
  };

  // Handler for customer group selection
  const handleGroupChange = (value: string) => {
    if (value === "create_new") {
      setIsCreateGroupModalOpen(true);
      return;
    }
    handleInputChange("group_id" as keyof CustomerEntity, parseInt(value));
  };

  // Handler for when modal closes without creating a group
  const handleCustomerGroupModalClose = (open: boolean) => {
    setIsCreateGroupModalOpen(open);
    if (!open) {
      setSelectKey((prev) => prev + 1);
    }
  };

  // Handler for when a new group is created
  const handleGroupCreated = (group: CustomerGroup) => {
    addCustomerGroup(group);
    handleInputChange("group_id" as keyof CustomerEntity, group.group_id);
  };

  // Helper function to get user-friendly field names
  const getFieldName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      customer_name: "Customer Name",
      gst_treatment: "GST Treatment",
      gstin_vat_tax_id: "GSTIN",
      pan_tax_registration_no: "PAN Tax ID",
      contact_name_1: "Primary Contact Name",
      contact_number_1: "Primary Contact Number",
      contact_email_1: "Primary Contact Email",
      contact_email_2: "Secondary Contact Email",
      billing_address: "Billing Address",
      city: "City",
      state: "State",
      country: "Country",
      postal_code: "Postal Code",
      credit_limit: "Credit Limit",
      payment_terms: "Payment Terms",
      currency_id: "Currency",
    };
    return fieldNames[field] || field.replace(/_/g, " ");
  };

  const validateForm = (): boolean => {
    // Clear all previous validation errors first
    clearErrors();

    const newErrors: Record<string, string> = {};

    // Required fields validation
    const requiredFields: (keyof CustomerEntity)[] = [
      "customer_name",
      "country",
      "payment_terms",
      "currency_id",
      "gst_treatment",
    ];

    requiredFields.forEach((field) => {
      if (
        formData[field] === undefined ||
        (typeof formData[field] === "string" &&
          formData[field]!.toString().trim() === "")
      ) {
        newErrors[field] = `${getFieldName(field)} is required`;
      }
    });

    // State is mandatory only when country is India
    if (formData.country?.toUpperCase() === "IN") {
      if (
        formData.state === undefined ||
        (typeof formData.state === "string" && formData.state.trim() === "")
      ) {
        newErrors.state = "State is required when country is India";
      }
    }

    // GSTIN validation (only if provided)
    if (
      formData.gstin_vat_tax_id &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.gstin_vat_tax_id,
      )
    ) {
      newErrors.gstin_vat_tax_id =
        "Please enter a valid GSTIN format (e.g., 27ABCDE1234F1Z5)";
    }

    // PAN validation (only if provided)
    if (
      formData.pan_tax_registration_no &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_tax_registration_no)
    ) {
      newErrors.pan_tax_registration_no =
        "Please enter a valid PAN format (e.g., ABCDE1234F)";
    }

    // Customer name validation (min 3 characters)
    if (formData.customer_name && formData.customer_name.length < 3) {
      newErrors.customer_name =
        "Customer name must be at least 3 characters long";
    }

    // Customer name validation (max 255 characters)
    if (formData.customer_name && formData.customer_name.length > 255) {
      newErrors.customer_name = "Customer name cannot exceed 255 characters";
    }

    // GST treatment validation
    const validGstTreatments = [
      "business_gst",
      "business_none",
      "overseas",
      "consumer",
    ];
    if (
      formData.gst_treatment &&
      !validGstTreatments.includes(formData.gst_treatment)
    ) {
      newErrors.gst_treatment =
        "GST treatment must be one of: business_gst, business_none, overseas, consumer";
    }

    // Contact name validation (max 255 characters)
    if (formData.contact_name_1 && formData.contact_name_1.length > 255) {
      newErrors.contact_name_1 = "Contact name cannot exceed 255 characters";
    }

    // Contact number validation (max 20 characters)
    if (formData.contact_number_1 && formData.contact_number_1.length > 20) {
      newErrors.contact_number_1 = "Contact number cannot exceed 20 characters";
    }

    // Primary email validation
    if (
      formData.contact_email_1 &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email_1)
    ) {
      newErrors.contact_email_1 = "Please provide a valid email address";
    }

    // Primary email length validation (max 255 characters)
    if (formData.contact_email_1 && formData.contact_email_1.length > 255) {
      newErrors.contact_email_1 = "Email cannot exceed 255 characters";
    }

    // Secondary email validation (optional but must be valid if provided)
    if (
      formData.contact_email_2 &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email_2)
    ) {
      newErrors.contact_email_2 =
        "Please provide a valid email address for secondary contact";
    }

    // Secondary email length validation (max 255 characters)
    if (formData.contact_email_2 && formData.contact_email_2.length > 255) {
      newErrors.contact_email_2 = "Email cannot exceed 255 characters";
    }

    // Postal code validation (max 20 characters)
    if (formData.postal_code && formData.postal_code.length > 20) {
      newErrors.postal_code = "Postal code cannot exceed 20 characters";
    }

    // Credit limit validation (decimal number)
    if (formData.credit_limit && isNaN(Number(formData.credit_limit))) {
      newErrors.credit_limit = "Credit limit must be a valid decimal number";
    }

    // Payment terms validation (integer days, max 99 — matches party forms)
    if (
      formData.payment_terms !== undefined &&
      formData.payment_terms !== null
    ) {
      const paymentTerms = Number(formData.payment_terms);
      if (isNaN(paymentTerms) || paymentTerms < 0) {
        newErrors.payment_terms = "Payment terms must be a positive number";
      } else if (paymentTerms > 99) {
        newErrors.payment_terms = "Payment terms cannot exceed 99 days";
      }
    }

    if (formData.gst_treatment === "business_gst") {
      if (!formData.gstin_vat_tax_id) {
        newErrors.gstin_vat_tax_id = "GSTIN is required for Business GST";
      } else if (
        gstinVerification.verificationRequired &&
        !gstinVerification.isVerified
      ) {
        newErrors.gstin_vat_tax_id = "Please verify the GSTIN before saving.";
      } else if (
        gstinVerification.isVerified &&
        gstinVerification.nameMismatch &&
        !gstinVerification.nameMatchConfirmed
      ) {
        newErrors.gstin_vat_tax_id =
          "GSTIN does not match Customer Name. Please confirm or update.";
      }
    }

    // Non-business_gst with GSTIN
    if (
      formData.gst_treatment !== "business_gst" &&
      formData.gstin_vat_tax_id &&
      formData.gstin_vat_tax_id.trim() !== ""
    ) {
      newErrors.gstin_vat_tax_id =
        "GST Number should only be entered for Business GST entities in India.";
    }

    // Set all validation errors at once (after clearing)
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    if (manualInvoiceReturn) {
      navigate(manualInvoiceReturn.createManualReturnPath, {
        state: { invoiceDraft: manualInvoiceReturn.invoiceDraft },
      });
      return;
    }

    if (customer) {
      const customerData = {
        customer_id: customer.customerid,
        zoho_customerid: customer.zoho_customerid,
        customer_name: customer.customer_name,
        gstin: customer.gstin_vat_tax_id,
        currency_id: customer.currency_id,
        gst_treatment: customer.gst_treatment,
        billing_address: customer.billing_address,
        address: customer.billing_address,
        payment_terms: customer.payment_terms,
        state: customer.state,
        country: customer.country,
      };

      console.log(
        "🔄 EditCustomer: Restoring original customer data on cancel",
      );

      sessionStorage.setItem(
        "autoSelectCustomer",
        JSON.stringify({
          customer: customerData,
          action: "cancelled",
          timestamp: Date.now(),
        }),
      );
    }

    navigate(-1);
  };

  const generateFieldChanges = (): Array<{
    field: string;
    old: string;
    new: string;
  }> => {
    if (!originalCustomer) return [];

    const changes: Array<{ field: string; old: string; new: string }> = [];
    const fieldsToTrack: (keyof CustomerEntity)[] = [
      "customer_name",
      "gst_treatment",
      "gstin_vat_tax_id",
      "pan_tax_registration_no",
      "contact_name_1",
      "contact_number_1",
      "contact_email_1",
      "contact_name_2",
      "contact_number_2",
      "contact_email_2",
      "communication_email_id",
      "billing_address",
      "shipping_address",
      "city",
      "state",
      "country",
      "postal_code",
      "credit_limit",
      "payment_terms",
      "currency_id",
      "bank_account_details",
      "group_id",
    ];

    fieldsToTrack.forEach((field) => {
      const oldVal = String(originalCustomer[field] || "");
      const newVal = String(formData[field] || "");
      if (oldVal !== newVal) {
        changes.push({ field, old: oldVal, new: newVal });
      }
    });

    return changes;
  };

  const handleReviewSuccess = () => {
    // ReviewRequestModal already showed success toast
    navigate("/dashboard/masters?tab=customers");
  };

  const handleSave = async () => {
    // Run validation first
    if (!validateForm()) {
      // ErrorBanner will display all validation errors and auto-scroll
      return;
    }

    // Check if review is required
    if (needsReview) {
      // Enrich formData with currency name/code for display
      const selectedCurrency = currencies.find(
        (c) => c.currency_id === formData.currency_id,
      );
      const enrichedFormData = {
        ...formData,
        currency_name: selectedCurrency?.currency_name,
        currency_code: selectedCurrency?.currency_code,
      };
      setPendingFormData(enrichedFormData);
      setShowReviewModal(true);
      return;
    }

    // Direct save (existing logic)
    setIsLoading(true);

    try {
      const selectedCurrency = currencies.find(
        (c) => c.currency_id === formData.currency_id,
      );
      const payload = {
        ...formData,
        currency_code: selectedCurrency?.currency_code,
        currency_name: selectedCurrency?.currency_name,
      };
      const customerEntity = await updateCustomer(customerId, payload);

      console.log("Customer updated successfully:", customerEntity);

      if (customerEntity) {
        if (
          customerContextData.final_context ||
          customerContextData.raw_input
        ) {
          try {
            await saveCustomerContext(customerIdInt, customerContextData);
          } catch (ctxError) {
            console.warn("Customer updated but context save failed:", ctxError);
          }
        }

        if (manualInvoiceReturn) {
          const company = getCompany();
          const vendorState = getStateAbbreviation(company?.state || "") || "";
          const applyCustomerPatch = buildManualInvoiceCustomerPatch(
            {
              zoho_customerid: customerEntity.zoho_customerid,
              customerid: customerEntity.customerid,
              customer_name: customerEntity.customer_name,
              gstin_vat_tax_id: customerEntity.gstin_vat_tax_id,
              state: customerEntity.state,
              country: customerEntity.country,
              billing_address: customerEntity.billing_address,
            },
            vendorState,
          );
          showSuccess("Customer updated successfully");
          navigate(manualInvoiceReturn.createManualReturnPath, {
            state: {
              invoiceDraft: manualInvoiceReturn.invoiceDraft,
              applyCustomerPatch,
            },
          });
          return;
        }

        const customerData = {
          customer_id: customerEntity.customerid,
          zoho_customerid: customerEntity.zoho_customerid,
          customer_name: customerEntity.customer_name,
          gstin: customerEntity.gstin_vat_tax_id,
          currency_id: customerEntity.currency_id,
          gst_treatment: customerEntity.gst_treatment,
          billing_address: customerEntity.billing_address,
          address: customerEntity.billing_address,
          payment_terms: customerEntity.payment_terms,
          state: customerEntity.state,
          country: customerEntity.country,
        };

        console.log("🎯 EditCustomer: Storing customer data for auto-select");

        sessionStorage.setItem(
          "autoSelectCustomer",
          JSON.stringify({
            customer: customerData,
            action: "updated",
            timestamp: Date.now(),
          }),
        );

        console.log("✅ Customer data stored, navigating back");
      }

      showSuccess("Customer updated successfully");

      navigate(-1);
    } catch (error) {
      console.error("Error updating customer:", error);
      // Display all API errors in ErrorBanner as global errors
      setApiErrors(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while fetching customer
  if (isFetchingCustomer) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader />
            <span className="text-body font-body text-subtext-color">
              Loading customer details...
            </span>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  // Show error if fetch failed
  if (fetchError) {
    return (
      <DefaultPageLayout>
        <div className="flex h-full w-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <span className="text-body font-body text-error-600">
              {fetchError}
            </span>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </div>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-6 py-6">
          <div className="flex items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Edit Customer
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral-tertiary"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isLoading || !canUpdate}
              loading={isLoading}
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-auto">
          <div className="flex w-full flex-col items-start gap-8 px-6 py-6">
            {/* Display validation errors with ErrorBanner component */}
            <div className="w-full pb-2">
              <ErrorBanner
                errors={errors}
                title="Please fix the following errors:"
                autoScrollOnMount={true}
                context="customer"
              />
            </div>

            {/* Customer Information */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Customer Information
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  label="Customer Name*"
                  helpText={errors.customer_name}
                  error={!!errors.customer_name}
                >
                  <TextField.Input
                    placeholder="Enter customer name"
                    maxLength={255}
                    value={formData.customer_name || ""}
                    onChange={(event) =>
                      handleInputChange("customer_name", event.target.value)
                    }
                    data-field="customer_name"
                  />
                </TextField>
                <Select
                  key={selectKey}
                  label="Customer Group"
                  placeholder={
                    customerGroupsLoading
                      ? "Loading groups..."
                      : "Select group (optional)"
                  }
                  helpText={customerGroupsError || ""}
                  error={!!customerGroupsError}
                  value={formData.group_id?.toString() || ""}
                  onValueChange={handleGroupChange}
                  disabled={customerGroupsLoading}
                >
                  {customerGroups.map((group) => (
                    <Select.Item
                      key={group.group_id}
                      value={group.group_id.toString()}
                    >
                      {group.group_name}
                    </Select.Item>
                  ))}
                  <Select.Item
                    value="create_new"
                    className="font-semibold text-brand-600"
                  >
                    + Create New Group
                  </Select.Item>
                </Select>
                <Select
                  label="GST Treatment*"
                  placeholder="Select GST treatment"
                  helpText={errors.gst_treatment}
                  error={!!errors.gst_treatment}
                  value={formData.gst_treatment}
                  onValueChange={(value) =>
                    handleInputChange("gst_treatment", value)
                  }
                  data-field="gst_treatment"
                >
                  <Select.Item value="business_gst">Business GST</Select.Item>
                  <Select.Item value="business_none">Business None</Select.Item>
                  <Select.Item value="overseas">Overseas</Select.Item>
                  <Select.Item value="consumer">Consumer</Select.Item>
                </Select>
                <GstinVerificationField
                  gstin={formData.gstin_vat_tax_id || ""}
                  gstTreatment={formData.gst_treatment || ""}
                  entityName={formData.customer_name || ""}
                  entityType="customer"
                  error={errors.gstin_vat_tax_id}
                  disabled={
                    formData.gst_treatment === "consumer" ||
                    formData.gst_treatment === "business_none" ||
                    formData.gst_treatment === "overseas"
                  }
                  verificationStatus={gstinVerification.status}
                  verificationData={gstinVerification.verificationData}
                  verificationRequired={gstinVerification.verificationRequired}
                  nameMismatch={gstinVerification.nameMismatch}
                  nameMatchConfirmed={gstinVerification.nameMatchConfirmed}
                  errorMessage={gstinVerification.errorMessage}
                  onGstinChange={(value) =>
                    handleInputChange("gstin_vat_tax_id", value)
                  }
                  onVerify={() => {
                    setUserTriggeredVerify(true);
                    gstinVerification.verify(formData.gstin_vat_tax_id || "");
                  }}
                  onConfirmNameMatch={() => {
                    gstinVerification.confirmNameMatch();
                    if (gstinVerification.verificationData?.legal_name) {
                      handleInputChange(
                        "customer_name",
                        gstinVerification.verificationData.legal_name,
                      );
                    }
                  }}
                />
                <div className="flex flex-col gap-1">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    Currency*
                  </span>
                  <SearchableDropdown
                    items={currencies}
                    loading={currenciesLoading}
                    trigger={
                      <Button
                        variant="neutral-secondary"
                        className="w-full justify-between"
                        iconRight={<FeatherChevronDown />}
                        disabled={currenciesLoading}
                      >
                        {currenciesLoading
                          ? "Loading currencies..."
                          : formData.currency_id
                            ? currencies.find(
                                (c) => c.currency_id === formData.currency_id,
                              )?.currency_name || "Select currency"
                            : "Select currency"}
                      </Button>
                    }
                    getSearchableText={(currency) => currency.currency_name}
                    renderItem={(currency) => currency.currency_name}
                    getItemKey={(currency) => currency.currency_id}
                    onSelect={(currency) =>
                      handleInputChange("currency_id", currency.currency_id)
                    }
                    selectedKey={formData.currency_id}
                    searchPlaceholder="Search currencies..."
                    emptyMessage="No currencies available"
                    noResultsMessage="No currencies match your search"
                  />
                  {(errors.currency_id || currenciesError) && (
                    <span className="text-caption font-caption text-error-700">
                      {errors.currency_id || currenciesError}
                    </span>
                  )}
                </div>
                <TextField
                  label="PAN ID"
                  helpText={errors.pan_tax_registration_no}
                  error={!!errors.pan_tax_registration_no}
                >
                  <TextField.Input
                    placeholder="Enter PAN"
                    maxLength={10}
                    value={formData.pan_tax_registration_no || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "pan_tax_registration_no",
                        event.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, ""),
                      )
                    }
                    data-field="pan_tax_registration_no"
                  />
                </TextField>
                <div className="flex flex-col gap-1">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    Country*
                  </span>
                  <SearchableDropdown
                    items={COUNTRIES}
                    trigger={
                      <Button
                        variant="neutral-secondary"
                        className="w-full justify-between"
                        iconRight={<FeatherChevronDown />}
                      >
                        {formData.country?.toUpperCase()
                          ? findCountryByCode(formData.country)?.name ||
                            "Select country"
                          : "Select country"}
                      </Button>
                    }
                    getSearchableText={(country) => country.name}
                    renderItem={(country) => country.name}
                    getItemKey={(country) => country.code}
                    onSelect={(country) =>
                      handleInputChange("country", country.code)
                    }
                    selectedKey={formData.country?.toUpperCase()}
                    searchPlaceholder="Search countries..."
                    emptyMessage="No countries available"
                    noResultsMessage="No countries match your search"
                  />
                  {errors.country && (
                    <span className="text-caption font-caption text-error-700">
                      {errors.country}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    {formData.country?.toUpperCase() === "IN"
                      ? "State*"
                      : "State"}
                  </span>
                  <SearchableDropdown
                    items={INDIAN_STATES}
                    trigger={
                      <Button
                        variant="neutral-secondary"
                        className="w-full justify-between"
                        iconRight={<FeatherChevronDown />}
                        disabled={formData.country?.toUpperCase() !== "IN"}
                      >
                        {formData.country?.toUpperCase() === "IN" &&
                        formData.state
                          ? findIndianStateByCode(formData.state)?.name ||
                            "Select state"
                          : "Select state"}
                      </Button>
                    }
                    getSearchableText={(state) => state.name}
                    renderItem={(state) => state.name}
                    getItemKey={(state) => state.code}
                    onSelect={(state) => handleInputChange("state", state.code)}
                    selectedKey={formData.state}
                    searchPlaceholder="Search states..."
                    emptyMessage="No states available"
                    noResultsMessage="No states match your search"
                    disabled={formData.country?.toUpperCase() !== "IN"}
                  />
                  {errors.state && (
                    <span className="text-caption font-caption text-error-700">
                      {errors.state}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Contact Information
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  label="Primary Contact Name"
                  helpText={errors.contact_name_1}
                  error={!!errors.contact_name_1}
                >
                  <TextField.Input
                    placeholder="Enter primary contact name"
                    maxLength={255}
                    value={formData.contact_name_1 || ""}
                    onChange={(event) =>
                      handleInputChange("contact_name_1", event.target.value)
                    }
                    data-field="contact_name_1"
                  />
                </TextField>
                <TextField
                  label="Primary Contact Number"
                  helpText={errors.contact_number_1}
                  error={!!errors.contact_number_1}
                >
                  <TextField.Input
                    placeholder="Enter primary contact number"
                    maxLength={20}
                    value={formData.contact_number_1 || ""}
                    onChange={(event) =>
                      handleInputChange("contact_number_1", event.target.value)
                    }
                    data-field="contact_number_1"
                  />
                </TextField>
                <TextField
                  label="Primary Contact Email"
                  helpText={errors.contact_email_1}
                  error={!!errors.contact_email_1}
                >
                  <TextField.Input
                    placeholder="Enter primary contact email"
                    maxLength={255}
                    value={formData.contact_email_1 || ""}
                    onChange={(event) =>
                      handleInputChange("contact_email_1", event.target.value)
                    }
                    data-field="contact_email_1"
                  />
                </TextField>
                <TextField
                  label="Secondary Contact Email"
                  helpText={errors.contact_email_2}
                  error={!!errors.contact_email_2}
                >
                  <TextField.Input
                    placeholder="Enter secondary contact email (optional)"
                    maxLength={255}
                    value={formData.contact_email_2 || ""}
                    onChange={(event) =>
                      handleInputChange("contact_email_2", event.target.value)
                    }
                    data-field="contact_email_2"
                  />
                </TextField>
              </div>
            </div>

            {/* Address Details */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Address Details
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  className="col-span-2"
                  label="Billing Address"
                  helpText={errors.billing_address}
                  error={!!errors.billing_address}
                >
                  <TextField.Input
                    placeholder="Enter billing address"
                    value={formData.billing_address || ""}
                    onChange={(event) =>
                      handleInputChange("billing_address", event.target.value)
                    }
                    data-field="billing_address"
                  />
                </TextField>
                <TextField
                  label="City"
                  helpText={errors.city}
                  error={!!errors.city}
                >
                  <TextField.Input
                    placeholder="Enter city"
                    maxLength={100}
                    value={formData.city || ""}
                    onChange={(event) =>
                      handleInputChange("city", event.target.value)
                    }
                    data-field="city"
                  />
                </TextField>
                <TextField
                  label="Postal Code"
                  helpText={errors.postal_code}
                  error={!!errors.postal_code}
                >
                  <TextField.Input
                    placeholder="Enter postal code"
                    maxLength={20}
                    value={formData.postal_code || ""}
                    onChange={(event) =>
                      handleInputChange("postal_code", event.target.value)
                    }
                    data-field="postal_code"
                  />
                </TextField>
              </div>
            </div>

            {/* Financial Information */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Financial Information
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  label="Credit Limit"
                  helpText={errors.credit_limit}
                  error={!!errors.credit_limit}
                >
                  <TextField.Input
                    placeholder="Enter credit limit (optional)"
                    type="number"
                    step="0.01"
                    value={formData.credit_limit?.toString() || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "credit_limit",
                        parseFloat(event.target.value) || 0,
                      )
                    }
                    data-field="credit_limit"
                  />
                </TextField>
                <TextField
                  label="Payment Terms (days)*"
                  helpText={errors.payment_terms || "Max 99 days"}
                  error={!!errors.payment_terms}
                >
                  <TextField.Input
                    className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    type="number"
                    min={0}
                    max={99}
                    placeholder="Enter payment terms in days"
                    value={formData.payment_terms?.toString() || ""}
                    onWheel={(event) => event.currentTarget.blur()}
                    onChange={(event) =>
                      handleInputChange(
                        "payment_terms",
                        Math.min(
                          99,
                          Math.max(0, parseInt(event.target.value) || 0),
                        ),
                      )
                    }
                    data-field="payment_terms"
                  />
                </TextField>
              </div>
            </div>

            {/* GL Classification Context */}
            <CustomerContextSection
              customerName={formData.customer_name || ""}
              existingContext={existingCustomerContext}
              onContextChange={setCustomerContextData}
            />
          </div>
        </div>
      </div>

      {/* Create Customer Group Modal */}
      <CreateCustomerGroupModal
        open={isCreateGroupModalOpen}
        onOpenChange={handleCustomerGroupModalClose}
        onGroupCreated={handleGroupCreated}
      />

      {/* Review Request Modal */}
      <ReviewRequestModal
        open={showReviewModal}
        onOpenChange={setShowReviewModal}
        moduleCode={MODULE_CODES.CUSTOMER}
        actionCode={ACTION_CODES.UPDATE}
        entityType="customer"
        entityId={customerIdInt ? customerIdInt : 0}
        onSuccess={handleReviewSuccess}
        changeDetails={{
          raw_json: pendingFormData,
          field_changes: generateFieldChanges(),
        }}
        skipNavigation={true}
      />

      {/* GSTIN Address Prefill Dialog */}
      <GSTINPrefillDialog
        isOpen={showPrefillDialog}
        entityType="customer"
        fields={buildCustomerGstinPrefillFields(
          formData,
          gstinVerification.verificationData,
          formData.gstin_vat_tax_id || "",
          currencies,
        )}
        onApplyAll={handleApplyGstinPrefill}
        onClose={() => setShowPrefillDialog(false)}
      />
    </DefaultPageLayout>
  );
};

export default EditCustomerPage;
